import moment from "moment";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useMount } from "react-use";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { Button, SaveButton, TextArea } from "../../components/common/Forms";
import { getCertificateData, saveCert } from "../../api";
import { API_URL } from "../../api/common";
import { useLocationQuery } from "../../util/common";
import Message from "../../components/common/Message";

const CertificateAddPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { lecture: id } = useLocationQuery();
  const [lecture_info, setLecture] = useState({
    name: "",
    title: "",
    date: null,
    time: "",
  });
  const [logoFile, setFile] = useState(null);
  const [info, setInfo] = useState({
    cId: null,
    lecture: { id, name: null, date: null, time: null },
    signature: { position: null, name: null },
    issue: { date: null },
    logo: { url: null },
    contents: [],
  });
  const [bLoaded, setLoaded] = useState({ lecture: false, info: false });
  const [isOpen, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  const certMttn = useMutation({
    mutationFn: saveCert,
    onSettled: (data, error) => {
      setOpen(false);
      setError(false);
      if (data.result) {
        setOpen(true);
      } else {
        setError(error || "수료증 저장 중 오류가 발생했습니다.");
      }
      queryClient.invalidateQueries({ queryKey: ["cert/save"] });
    },
  });

  const handleLogoChange = (e) => {
    setError(false);
    setFile(e.target.files[0]);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      // document.getElementById("logo_preview").src = e.target.value;
    };
    reader.readAsDataURL(e.target.files[0]);
  };

  const handleCertContent = (e) => {
    setError(false);
    setInfo({ ...info, contents: e.target.value.split(/\r|\r\n|\n/) });
  };
  const handleCertInfo = (e) => {
    setError(false);
    const { id, value } = e.target;
    const [key, prop, sub] = id.split("_");
    if (sub) {
      setInfo({
        ...info,
        [key]: { ...info[key], [prop]: { ...info[key][prop], [sub]: value } },
      });
    } else {
      setInfo({ ...info, [key]: { ...info[key], [prop]: value } });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const form = e.target;
    const areAllFieldsFilled = Array.from(form.elements).every((element) =>
      element.checkValidity()
    );

    if (areAllFieldsFilled) {
      certMttn.mutate(logoFile ? { ...info, logoFile } : info);
    }
  };

  useMount(async () => {
    if (id) {
      const res = await queryClient.fetchQuery([`certificate/${id}`], {
        queryFn: () => getCertificateData({ id }),
      });
      const { result, data, error: e } = res;
      if (result) {
        const {
          _id: cId,
          issued_date: iss,
          logo,
          lecture: lec,
          signature: sig,
          contents: con,
        } = data;
        setLecture({
          name: lec.name,
          title: `${lec.name} 수료증 정보`,
          date: [new moment(lec.date.start), new moment(lec.date.end)],
          time: lec.time,
        });
        const [hour, min] = lec.time
          .split("시간")
          .map((v) => Number(v.replace(/[^\d]/g, "")));
        setInfo({
          cId,
          lecture: {
            id,
            name: lec.name,
            date: {
              start: new moment(lec.date.start).format("yyyy-MM-DDTHH:mm:00"),
              end: new moment(lec.date.end).format("yyyy-MM-DDTHH:mm:00"),
            },
            time: { hour, min },
          },
          signature: sig,
          issue: { date: new moment(iss).format("yyyy-MM-DD") },
          logo: {
            url: `${API_URL()}certificate/${logo}`,
            name: logo ? `${logo}.png` : "",
          },
          contents: con,
        });
        setLoaded({ lecture: true, info: true });
      } else if (data) {
        const { lecture: lec } = data;
        setLecture({
          name: lec.name,
          title: `${lec.name} 수료증 정보`,
          date: [new moment(lec.date.start), new moment(lec.date.end)],
          time: lec.time,
        });
        setLoaded({ lecture: true, info: false });
      } else {
        setError(e);
        setLoaded({ lecture: false, info: false });
      }
    }
  });

  const handleClose = () => {
    setOpen(false);
    return navigate("/lecture");
  };

  return (
    <section className="min-h-screen overflow-y-auto md:mt-4">
      {bLoaded.lecture && (
        <div className="w-full mx-auto shadow-md overflow-hidden md:rounded-lg md:w-9/12">
          <div className="p-4 bg-cyan-500 ">
            <div className="mx-auto md:w-full md:mx-0">
              <header className="flex-1 w-full text-3xl text-white font-bold">
                {lecture_info.title}
              </header>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="space-y-6 rounded-b-lg bg-white pb-6">
              <hr />
              {bLoaded.lecture && (
                <div className="items-center w-full px-4">
                  <h2 className="mx-auto text-lg font-bold">강의 정보</h2>
                  <div className="inline-flex flex-wrap items-center gap-2">
                    <label className="text-sm font-bold min-w-[6rem] text-right">
                      강의명
                    </label>
                    <p className="py-2">{lecture_info.name}</p>
                  </div>
                  <div className="inline-flex flex-wrap items-center gap-2">
                    <label className="text-sm font-bold min-w-[6rem] text-right">
                      강의날짜
                    </label>
                    {lecture_info.date && (
                      <div className="inline-flex flex-wrap items-center gap-2 py-2">
                        <p className="flex-initial">
                          {lecture_info.date[0].format("LLL")}
                        </p>
                        <span> ~ </span>
                        <p>{lecture_info.date[1].format("LLL")}</p>
                        <p>{lecture_info.time}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <hr />
              <div className="flex flex-col w-full px-4 gap-2">
                <h2 className="text-lg font-bold">과정 정보</h2>
                <div className="inline-flex flex-wrap items-center gap-2 w-full">
                  <label className="text-sm font-bold min-w-[6rem] text-right">
                    과정명
                    <span className="ordinal text-red-600"> *</span>
                  </label>
                  <input
                    id="lecture_name"
                    type="text"
                    className="appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm flex-1 w-full p-2"
                    placeholder="수료증의 과정명을 입력하세요."
                    onChange={handleCertInfo}
                    defaultValue={info.lecture.name}
                    required
                  />
                </div>
                <div className="inline-flex flex-wrap items-baseline gap-2 w-full">
                  <label className="text-sm font-bold min-w-[6rem] text-right">
                    수료기간
                    <span className="ordinal text-red-600"> *</span>
                  </label>
                  <div className="flex flex-row flex-wrap gap-2 flex-1">
                    <div className="inline-flex items-center gap-2 flex-1">
                      <h3 className="text-sm font-bold w-8 text-end text-cyan-600">
                        시작
                      </h3>
                      <input
                        id="lecture_date_start"
                        type="datetime-local"
                        className="appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm p-2 w-full"
                        onChange={handleCertInfo}
                        defaultValue={
                          info.lecture.date ? info.lecture.date.start : ""
                        }
                        required
                      />
                    </div>
                    <div className="inline-flex items-center gap-2 flex-1">
                      <h3 className="text-sm font-bold w-8 text-end text-cyan-600">
                        끝
                      </h3>
                      <input
                        id="lecture_date_end"
                        type="datetime-local"
                        className="appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm p-2 w-full"
                        onChange={handleCertInfo}
                        defaultValue={
                          info.lecture.date ? info.lecture.date.end : ""
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="inline-flex flex-wrap items-baseline gap-2 w-full">
                  <label className="text-sm font-bold min-w-[6rem] text-right">
                    수료시간
                    <span className="ordinal text-red-600"> *</span>
                  </label>
                  <div className="inline-flex items-center gap-2">
                    <input
                      id="lecture_time_hour"
                      type="number"
                      min={0}
                      max={99}
                      className="appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm p-2 w-1/2 md:w-1/4"
                      onChange={handleCertInfo}
                      defaultValue={
                        info.lecture.time ? info.lecture.time.hour : ""
                      }
                      required
                    />
                    <h3 className="text-sm font-bold w-8">시간</h3>
                    <input
                      id="lecture_time_min"
                      type="number"
                      min={0}
                      max={59}
                      className="appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm p-2 w-1/2 md:w-1/4"
                      onChange={handleCertInfo}
                      defaultValue={
                        info.lecture.time ? info.lecture.time.min : ""
                      }
                      required
                    />
                    <h3 className="text-sm font-bold w-8">분</h3>
                  </div>
                </div>
              </div>
              <div className="flex flex-col w-full px-4 gap-2">
                <h2 className="text-lg font-bold">
                  발급 내용<span className="ordinal text-red-600"> *</span>
                </h2>
                <TextArea
                  className="flex-1 w-full p-2 bg-white shadow-sm text-sm"
                  rows={5}
                  value={info.contents.join("\n")}
                  onChange={handleCertContent}
                  required
                ></TextArea>
              </div>
              <div className="flex flex-col w-full px-4 gap-2">
                <h2 className="text-lg font-bold">발급 정보</h2>
                <div className="inline-flex flex-wrap items-baseline gap-2 w-full">
                  <label className="text-sm font-bold min-w-[6rem] text-right">
                    발급일자
                    <span className="ordinal text-red-600"> *</span>
                  </label>
                  <input
                    id="issue_date"
                    type="date"
                    className="appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm p-2 flex-1"
                    onChange={handleCertInfo}
                    defaultValue={info.issue.date}
                    required
                  />
                </div>
                <div className="inline-flex flex-wrap items-baseline gap-2 w-full">
                  <label className="text-sm font-bold min-w-[6rem] text-right">
                    직인
                  </label>
                  <div className="flex flex-row flex-wrap gap-2 flex-1">
                    <div className="inline-flex items-center gap-2 flex-1">
                      <h3 className="text-sm font-bold w-8 text-end text-cyan-600">
                        직위
                      </h3>
                      <input
                        id="signature_position"
                        type="text"
                        className="appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm p-2 w-full"
                        onChange={handleCertInfo}
                        defaultValue={info.signature.position}
                      />
                    </div>
                    <div className="inline-flex items-center gap-2 flex-1">
                      <h3 className="text-sm font-bold w-8 text-end text-cyan-600">
                        성명
                      </h3>
                      <input
                        id="signature_name"
                        type="text"
                        className="appearance-none bg-white border border-zinc-300 placeholder-zinc-400 rounded-lg shadow-sm text-sm p-2 w-full"
                        onChange={handleCertInfo}
                        defaultValue={info.signature.name}
                      />
                    </div>
                  </div>
                </div>
                <div className="inline-flex flex-wrap items-baseline gap-2 w-full">
                  <label className="text-sm font-bold min-w-[6rem] text-right">
                    이미지
                  </label>
                  <input
                    type="file"
                    name="logo_upload"
                    accept="image/*"
                    className="file-input file-input-bordered file-input-accent file-input-sm"
                    onChange={handleLogoChange}
                  />
                  <div className="inline-flex flex-wrap self-start gap-2 md:w-full md:justify-center">
                    {(preview || info.logo.url) && (
                      <img
                        src={preview || info.logo.url}
                        alt="logo"
                        className="h-24 w-auto"
                        id="logo_preview"
                      />
                    )}
                  </div>
                </div>
              </div>
              <hr />
              <div className="w-full px-4 ml-auto">
                <SaveButton>저장</SaveButton>
              </div>
            </div>
          </form>
        </div>
      )}
      {error && <Message type="error">{error}</Message>}
      <Modal
        className="h-full text-center flex"
        isOpen={isOpen}
        onRequestClose={handleClose}
        ariaHideApp={false}
      >
        <div className="card w-96 bg-white shadow-xl m-auto">
          <div className="card-body">
            <h2 className="card-title">{lecture_info.title}</h2>
            <p>
              {info.cId
                ? "수료증 정보가 정상적으로 수정되었습니다."
                : "수료증 정보가 정상적으로 등록되었습니다."}
            </p>
            <div className="card-actions justify-center mt-4">
              <Button type="primary" block onClick={handleClose}>
                확인
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default CertificateAddPage;
